import React, { useEffect, useRef } from "react";

import { useDeviceContext } from "../../../context/DeviceContext";
import { useUserContext } from "../../../hooks/useUserContext";
import { dmnIdentity } from "../../../utils/dmnIdentity";
import {
  getEnvironmentProps,
  GetEnvironmentPropsResponse
} from "../../../utils/environment";

interface FeatureSentimentProps {
  feature: string;
  dismissible?: boolean;
}

const FeatureSentiment: React.FC<FeatureSentimentProps> = ({
  feature,
  dismissible = false
}) => {
  const { user } = useUserContext();
  const { theme } = useDeviceContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const [envConfig, setEnvConfig] =
    React.useState<GetEnvironmentPropsResponse | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const token = await dmnIdentity.getAccessToken();
        const envProps = getEnvironmentProps(token);
        setEnvConfig(envProps);
      } catch (error) {
        console.error("Failed to load environment config:", error);
      }
    };
    loadConfig();
  }, []);

  useEffect(() => {
    if (
      !envConfig?.FEATURE_SENTIMENT_API_HOST ||
      !envConfig?.FEATURE_SENTIMENT_ACCOUNT_KEY ||
      !envConfig?.FEATURE_SENTIMENT_REGISTRY_URL ||
      !user?._id ||
      !containerRef.current
    ) {
      return;
    }

    const customerName = [user.first_name, user.last_name]
      .filter(Boolean)
      .join(" ");
    const sentimentTheme = theme === "Dark" ? "dark" : "light";

    const params = new URLSearchParams({
      feature: feature,
      apiHost: envConfig.FEATURE_SENTIMENT_API_HOST,
      accountToken: envConfig.FEATURE_SENTIMENT_ACCOUNT_KEY,
      product: "Collins",
      customerName: customerName,
      userId: user._id,
      roleId: "",
      language: "en-GB",
      theme: sentimentTheme,
      dismissible: dismissible.toString()
      /*
        Parameters type: integer in day with range from 0 to 365
        Parameters value: 0 = always show, 30 (default)
      // reDisplayAfterDays: "30"
      */
    });

    const componentUrl = `${envConfig.FEATURE_SENTIMENT_REGISTRY_URL}/evo-feature-feedback-collector/1/?${params.toString()}`;

    const ocComponent = document.createElement("oc-component");
    ocComponent.setAttribute("href", componentUrl);

    containerRef.current.innerHTML = "";
    containerRef.current.appendChild(ocComponent);

    if (window.oc?.renderNestedComponent) {
      window.oc.renderNestedComponent(
        containerRef.current.querySelector("oc-component")
      );
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [envConfig, user, feature, theme]);

  if (
    !user?._id ||
    !envConfig?.FEATURE_SENTIMENT_API_HOST ||
    !envConfig?.FEATURE_SENTIMENT_ACCOUNT_KEY ||
    !envConfig?.FEATURE_SENTIMENT_REGISTRY_URL
  ) {
    return null;
  }

  return <div ref={containerRef} className="feature-sentiment-container" />;
};

export default FeatureSentiment;
